import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp, orderBy } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate, useSearchParams } from "react-router-dom";

function DistributorToDsBilling() {
  const [userProfile, setUserProfile] = useState(null);
  const [dsList, setDsList] = useState([]);
  const [skuMaster, setSkuMaster] = useState({});
  const [skuList, setSkuList] = useState([]);
  const [items, setItems] = useState({});
  const [selectedDs, setSelectedDs] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate("/"); return; }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        setUserProfile(profile);
        loadData(profile);
      }
    });
    return () => unsub();
  }, [navigate]);

  const loadData = async (profile) => {
    setLoading(true);
    try {
      // 1. Load DS List
      let dsQ;
      if (profile.role === "superadmin") {
          dsQ = query(collection(db, "dsMaster"));
      } else {
          dsQ = query(collection(db, "dsMaster"), where("distributorId", "==", profile.partyId || ""));
      }
      const dsSnap = await getDocs(dsQ);
      const dsArr = dsSnap.docs.map(d => d.data());
      setDsList(dsArr);

      // 2. Load SKU Master
      const skuQ = query(collection(db, "skuMaster"));
      const skuSnap = await getDocs(skuQ);
      const smap = {};
      const slist = [];
      const itemMap = {};
      skuSnap.forEach(d => {
        const data = d.data();
        const skuKey = String(data.sku || data.Name || "").trim().toUpperCase();
        smap[skuKey] = data;
        slist.push(skuKey);
        itemMap[skuKey] = { bag: "", ctn: "", pkt: "" };
      });
      
      // Secondary sorting in JS to avoid index requirement
      slist.sort((a,b) => (Number(smap[a].sortorder) || 999) - (Number(smap[b].sortorder) || 999));

      setSkuMaster(smap);
      setSkuList(slist);
      setItems(itemMap);

      const urlDs = searchParams.get("dsId");
      if (urlDs) setSelectedDs(urlDs);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const financialSummary = useMemo(() => {
    let tQty = 0, tBasic = 0, tGst = 0, tCess = 0, tGrand = 0;
    Object.entries(items).forEach(([sku, units]) => {
      const m = skuMaster[sku];
      if (!m) return;
      
      const c_Bag = Number(m.cartonsPerBag) || 0;
      const c_Ctn = Number(m.packetsPerCarton) || 0;

      const totalPkt = (Number(units.bag || 0) * (c_Bag || 1) * (c_Ctn || 1)) + 
                       (Number(units.ctn || 0) * (c_Ctn || 1)) + 
                       Number(units.pkt || 0);

      if (totalPkt <= 0) return;
      
      const rate = Number(m.dsBasicRate || m.Basic_Eway || 0);
      const basic = totalPkt * rate;
      
      // TAX CALCULATION: Fixed to 5% of Basic (Exclusive) as per user request
      const gstRate = Number(m.gst) || 5;
      const gst = basic * (gstRate / 100);
      
      const cess = basic * (Number(m.cess || 0) / 100);
      const addl = totalPkt * (Number(m.addlcess || 0));
      const rebate = totalPkt * (Number(m.Rebate || 0));

      const lineTotal = basic + gst + cess + addl + rebate;

      tQty += totalPkt;
      tBasic += basic;
      tGst += gst;
      tCess += (cess + addl + rebate);
      tGrand += lineTotal;
    });
    return { tQty, tBasic, tGst, tCess, tGrand };
  }, [items, skuMaster]);

  const handleSaveOrder = async () => {
    if (!selectedDs) { alert("Please select a Salesman (DS)"); return; }
    if (financialSummary.tQty === 0) { alert("Please enter quantities"); return; }
    if (submitting) return;

    setSubmitting(true);
    try {
      const cleanItems = {};
      Object.entries(items).forEach(([sku, units]) => {
        const skuUpper = String(sku).trim().toUpperCase();
        const m = skuMaster[skuUpper];
        const c_Bag = Number(m.cartonsPerBag) || 1;
        const c_Ctn = Number(m.packetsPerCarton) || 1;
          const totalPkt = (Number(units.bag || 0) * c_Bag * c_Ctn) + 
                           (Number(units.ctn || 0) * c_Ctn) + 
                           Number(units.pkt || 0);
        if (totalPkt > 0) cleanItems[skuUpper] = totalPkt;
      });

      const dsObj = dsList.find(d => d.dsId === selectedDs);

      const docRef = await addDoc(collection(db, "orders"), {
        clientId: userProfile.clientId,
        distributorId: userProfile.partyId || "",
        partyId: selectedDs,
        partyName: dsObj?.name || selectedDs,
        items: cleanItems,
        totalQty: financialSummary.tQty,
        totalAmount: financialSummary.tGrand,
        source: "DISTRIBUTOR_TO_DS",
        status: "PENDING",
        createdAt: serverTimestamp(),
      });

      alert("Billing Saved! Redirecting to Approval...");
      navigate(`/approve-order/${docRef.id}`);
    } catch (err) {
      console.error(err);
      alert("Error saving: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <Layout>
      <div style={{ padding: 100, textAlign: "center", color: "var(--text-muted)" }}>
        Loading Logistics Engine...
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="layout-content">
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
                <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Logistics Provisioning</h1>
                <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Deploy inventory to salesmen, manage fleet billing, and authorize regional distribution.</p>
            </div>
            <div style={{ display: "flex", gap: 30 }}>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--primary)" }}>{financialSummary.tQty}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Gross Units</div>
                </div>
                <div style={{ width: 1, height: 40, background: "var(--border)" }}></div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--success)" }}>₹{financialSummary.tGrand.toLocaleString()}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Load Value</div>
                </div>
            </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 32 }}>
          {/* MAIN BILLING INTERFACE */}
          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            
            {/* DESTINATION SELECTION */}
            <div className="premium-card">
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase" }}>Target Sales Executive</label>
                <select 
                    className="form-input"
                    value={selectedDs} 
                    onChange={e => setSelectedDs(e.target.value)} 
                    style={{ width: "100%", padding: 14 }}
                >
                    <option value="">-- Choose Salesman from Registry --</option>
                    {dsList.map(ds => <option key={ds.dsId} value={ds.dsId}>{ds.name} ({ds.dsId})</option>)}
                </select>
              </div>
            </div>

            {/* PRODUCT MATRIX */}
            <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-hover)", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "var(--text-main)" }}>INVENTORY MANIFEST</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>PRICING TIER: SALESMAN (DS)</span>
              </div>
              <div style={{ maxHeight: 600, overflowY: "auto" }}>
                <table className="enterprise-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Code</th>
                      <th>Product Blueprint</th>
                      <th style={{ textAlign: "right" }}>DS Rate</th>
                      <th style={{ width: 120, textAlign: "center" }}>Quantity</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skuList.map((sku) => {
                        const m = skuMaster[sku];
                        const rate = Number(m.dsBasicRate || m.Basic_Eway || 0);
                        const iUnits = items[sku] || { bag: "", ctn: "", pkt: "" };
                        const c_Bag = Number(m.cartonsPerBag) || 1;
                        const c_Ctn = Number(m.packetsPerCarton) || 1;
                        
                        const rowPkt = (Number(iUnits.bag || 0) * c_Bag * c_Ctn) + 
                                       (Number(iUnits.ctn || 0) * c_Ctn) + 
                                       Number(iUnits.pkt || 0);
                        
                        const sub = rowPkt * rate;

                        return (
                          <tr key={sku}>
                            <td style={{ fontWeight: 800, color: "var(--primary)", fontSize: 12 }}>{sku}</td>
                            <td>
                              <div style={{ fontWeight: 700, fontSize: 14 }}>{m.Name}</div>
                              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>MRP: ₹{m.mrp} | GST: {m.gst}%</div>
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 600 }}>₹{rate.toFixed(2)}</td>
                            <td style={{ textAlign: "center" }}>
                                <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                    <div>
                                        <div style={{ fontSize: 9, color: "var(--text-muted)" }}>BAG</div>
                                        <input 
                                            type="number" 
                                            className="form-input"
                                            value={iUnits.bag} 
                                            onChange={e => setItems({ ...items, [sku]: { ...iUnits, bag: e.target.value } })}
                                            style={{ width: 50, textAlign: "center", fontWeight: 800, padding: 6, fontSize: 13 }}
                                        />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 9, color: "var(--text-muted)" }}>CTN</div>
                                        <input 
                                            type="number" 
                                            className="form-input"
                                            value={iUnits.ctn} 
                                            onChange={e => setItems({ ...items, [sku]: { ...iUnits, ctn: e.target.value } })}
                                            style={{ width: 50, textAlign: "center", fontWeight: 800, padding: 6, fontSize: 13 }}
                                        />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 9, color: "var(--text-muted)" }}>PKT</div>
                                        <input 
                                            type="number" 
                                            className="form-input"
                                            value={iUnits.pkt} 
                                            onChange={e => setItems({ ...items, [sku]: { ...iUnits, pkt: e.target.value } })}
                                            style={{ width: 50, textAlign: "center", fontWeight: 800, padding: 6, fontSize: 13 }}
                                        />
                                    </div>
                                </div>
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 800, color: "var(--text-main)" }}>₹{sub.toFixed(2)}</td>
                          </tr>
                        );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* SIDEBAR: SUMMARY & AUTHORIZATION */}
          <div style={{ position: "sticky", top: 100 }}>
            <div className="premium-card" style={{ background: "var(--text-main)", color: "white", padding: 28 }}>
              <h3 style={{ margin: "0 0 24px 0", fontSize: 13, fontWeight: 800, letterSpacing: "0.05em", opacity: 0.6 }}>LOAD SUMMARY</h3>
              
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                  <span style={{ opacity: 0.7 }}>Net Basic</span>
                  <span style={{ fontWeight: 700 }}>₹{financialSummary.tBasic.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                  <span style={{ opacity: 0.7 }}>GST Input</span>
                  <span style={{ fontWeight: 700 }}>₹{financialSummary.tGst.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                  <span style={{ opacity: 0.7 }}>Cess/Other</span>
                  <span style={{ fontWeight: 700 }}>₹{financialSummary.tCess.toFixed(2)}</span>
                </div>
                
                <div style={{ width: "100%", height: 1, background: "rgba(255,255,255,0.1)", margin: "8px 0" }}></div>
                
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.7 }}>FINAL AMOUNT</span>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 32, fontWeight: 900, color: "var(--success)" }}>₹{financialSummary.tGrand.toFixed(2)}</div>
                    <div style={{ fontSize: 10, opacity: 0.5 }}>AUTHORIZATION PENDING</div>
                  </div>
                </div>

                <button 
                    onClick={handleSaveOrder}
                    disabled={submitting}
                    className="btn-primary"
                    style={{ 
                        width: "100%", 
                        padding: 18, 
                        marginTop: 12, 
                        fontSize: 16, 
                        background: "var(--success)",
                        border: "none",
                        color: "white"
                    }}
                >
                    {submitting ? "PROCESSING EXECUTION..." : "AUTHORIZE SHIPMENT"}
                </button>
                
                <div style={{ textAlign: "center", fontSize: 11, opacity: 0.5 }}>
                  This action will generate a pending order for review and document generation.
                </div>
              </div>
            </div>
            
            <div style={{ marginTop: 20, padding: 20, borderRadius: 20, background: "rgba(0,0,0,0.03)", border: "1px dashed var(--border)" }}>
               <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text-muted)", marginBottom: 4 }}>OPERATIONAL NOTE</div>
               <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
                 Selected salesman will receive inventory sync updates upon approval of this shipment by the audit department.
               </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default DistributorToDsBilling;
